import React from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import { companyInfo, agreementSections } from './agreementContent';

import css from './AgreementPage.module.css';
import { FormattedMessage, useIntl } from 'react-intl';

import { ReactComponent as DownloadIcon } from '../../assets/svg/downloadIcon.svg';

const Paragraph = ({ id }) => (
  <p className={css.paragraph}>
    <FormattedMessage id={id} />
  </p>
);

// Bolds the lead-in before the first colon, e.g. "Buyer: the individual
// purchasing a Listing" -> **Buyer:** the individual purchasing a Listing.
const BoldLeadText = ({ text }) => {
  const colonIndex = text.indexOf(':');
  if (colonIndex === -1) {
    return text;
  }

  return (
    <>
      <span className={css.boldLead}>{text.slice(0, colonIndex + 1)}</span>
      {text.slice(colonIndex + 1)}
    </>
  );
};

const BulletList = ({ itemIds }) => (
  <ul className={css.bulletList}>
    {itemIds.map(id => (
      <li key={id}>
        <FormattedMessage id={id}>{chunks => <BoldLeadText text={chunks.join('')} />}</FormattedMessage>
      </li>
    ))}
  </ul>
);

const OrderedList = ({ itemIds }) => (
  <ol className={css.orderedList}>
    {itemIds.map(id => (
      <li key={id}>
        <FormattedMessage id={id}>{chunks => <BoldLeadText text={chunks.join('')} />}</FormattedMessage>
      </li>
    ))}
  </ol>
);

const RefundTable = ({ table }) => (
  <div className={css.tableWrap}>
    <table className={css.table}>
      <thead>
        <tr>
          {table.headerIds.map(id => (
            <th key={id}>
              <FormattedMessage id={id} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {table.rows.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {row.map(id => (
              <td key={id}>
                <FormattedMessage id={id} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const BoxBody = ({ body }) => (
  <>
    {body.id ? <Paragraph id={body.id} /> : null}
    {body.introId ? <Paragraph id={body.introId} /> : null}
    {body.paragraphs ? body.paragraphs.map(id => <Paragraph id={id} key={id} />) : null}
    {body.table ? <RefundTable table={body.table} /> : null}
    {body.list ? <BulletList itemIds={body.list} /> : null}
    {body.orderedList ? <OrderedList itemIds={body.orderedList} /> : null}
    {body.outroId ? <Paragraph id={body.outroId} /> : null}
  </>
);

const Box = ({ number, titleId, body }) => (
  <div className={css.box}>
    {number || titleId ? (
      <p className={css.boxNumber}>
        {number} {titleId ? <FormattedMessage id={titleId} /> : null}
      </p>
    ) : null}
    <BoxBody body={body} />
  </div>
);

const Callout = ({ titleId, textId }) => (
  <div className={css.callout}>
    {titleId ? (
      <h3 className={css.calloutTitle}>
        <FormattedMessage id={titleId} />
      </h3>
    ) : null}
    <Paragraph id={textId} />
  </div>
);

const ContactSection = ({ cards }) => (
  <div className={css.contactGrid}>
    {cards.map(card => (
      <div className={css.contactCard} key={card.headingId}>
        <p className={css.contactCardHeading}>
          <FormattedMessage id={card.headingId} />
        </p>
        {card.rows.map((row, index) => (
          <div className={css.contactRow} key={index}>
            {row.labelId ? (
              <span className={css.contactLabel}>
                <FormattedMessage id={row.labelId} />
              </span>
            ) : null}
            <span className={css.contactValue}>{row.value}</span>
          </div>
        ))}
      </div>
    ))}
  </div>
);

const Block = ({ block }) => {
  switch (block.type) {
    case 'p':
      return <Paragraph id={block.id} />;
    case 'ul':
      return <BulletList itemIds={block.itemIds} />;
    case 'ol':
      return <OrderedList itemIds={block.itemIds} />;
    case 'box':
      return <Box number={block.number} titleId={block.titleId} body={block.body} />;
    case 'callout':
      return <Callout titleId={block.titleId} textId={block.textId} />;
    case 'contact':
      return <ContactSection cards={block.cards} />;
    default:
      return null;
  }
};

const Section = ({ section }) => {
  const content = (
    <>
      <h2 className={css.sectionHeading}>
        {section.number}. <FormattedMessage id={section.titleId} />
      </h2>
      {section.blocks.map((block, index) => (
        <Block block={block} key={index} />
      ))}
    </>
  );

  return (
    <div className={css.section}>
      {section.highlight ? (
        <div className={css.highlightBlock}>{content}</div>
      ) : section.gradient ? (
        <div className={css.gradientBlock}>{content}</div>
      ) : section.accentBar ? (
        <div className={css.accentBarBlock}>{content}</div>
      ) : (
        content
      )}
    </div>
  );
};

// A terms item is either a full section (has a number/title/blocks) or a
// standalone block (e.g. the crisis callout) that isn't part of any section.
const TermsItem = ({ item }) =>
  item.number ? (
    <Section section={item} />
  ) : (
    <div className={css.section}>
      <Block block={item} />
    </div>
  );

/**
 * Content-only rendering of the Terms & Conditions, meant for embedding
 * elsewhere on the site (e.g. inside the sign-up modal).
 */
export const AgreementContent = () => {
  const contactIndex = agreementSections.findIndex(item => item.number === '24');
  const cardItems =
    contactIndex === -1 ? agreementSections : agreementSections.slice(0, contactIndex);
  const afterCardItems = contactIndex === -1 ? [] : agreementSections.slice(contactIndex);

  return (
    <>
      <div className={css.card}>
        {cardItems.map((item, index) => (
          <TermsItem item={item} key={item.number || `standalone-${index}`} />
        ))}
      </div>
      {afterCardItems.length > 0 ? (
        <div className={css.cardFooter}>
          {afterCardItems.map((item, index) => (
            <TermsItem item={item} key={item.number || `standalone-footer-${index}`} />
          ))}
        </div>
      ) : null}
    </>
  );
};

export const AgreementPageComponent = props => {
  const { scrollingDisabled } = props;
  const history = useHistory();
  const intl = useIntl();

  const handleDownloadPdf = () => {
    if (typeof window !== 'undefined') {
      window.print();
    }
  };

  const pageTitle = intl.formatMessage({ id: 'AgreementPage.pageTitle' });

  return (
    <Page title={pageTitle} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <div className={css.heroSection}>
            <div className={css.topRow}>
              <button className={css.backLink} onClick={() => history.goBack()} type="button">
                <FormattedMessage id="AgreementPage.backBtn" />
              </button>
              <button className={css.downloadButton} onClick={handleDownloadPdf} type="button">
                <DownloadIcon className={css.icon} />
                <FormattedMessage id="AgreementPage.downloadBtn" />
              </button>
            </div>

            <h1 className={css.title}>{pageTitle}</h1>
            <div className={css.subtitleRow}>
              <span>{companyInfo.name}</span>
              <span className={css.subtitleDivider}>|</span>
              <span>
                <FormattedMessage id={companyInfo.registrationLabelId} /> -{' '}
                <span className={css.registrationNumber}>{companyInfo.registrationNumber}</span>
              </span>
            </div>
          </div>

          <div className={css.content}>
            <AgreementContent />
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const AgreementPage = connect(mapStateToProps)(AgreementPageComponent);

export default AgreementPage;
